"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { LogOut, MessageCircle, Plus, UsersRound } from "lucide-react";

import { BrandLogo } from "@/components/brand/brand-logo";
import { GlobalSearch } from "@/components/layout/global-search";
import { useSidebarState } from "@/components/layout/sidebar-state";
import { NotificationBell } from "@/components/notifications/notification-dropdown";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/ui/user-avatar";
import { useCurrentUser } from "@/hooks/use-current-user";
import { signOut } from "@/lib/auth/api";
import { cn } from "@/lib/utils";

export function DashboardHeader() {
  const router = useRouter();
  const currentUserQuery = useCurrentUser();
  const user = currentUserQuery.data;
  const { collapsed } = useSidebarState();

  async function handleSignOut() {
    await signOut();
    router.replace("/login");
    router.refresh();
  }

  return (
    <header className="sticky top-0 z-40 w-full border-b border-black/[0.04] bg-card">
      <div className="flex h-16 w-full items-center gap-4 px-4 md:gap-6 md:pr-6 md:pl-0">
        <div
          className={cn(
            "flex items-center md:h-full md:shrink-0 md:transition-[width] md:duration-200",
            collapsed
              ? "md:w-[76px] md:justify-center md:px-0"
              : "md:w-[248px] md:px-6",
          )}
        >
          <BrandLogo compact={collapsed} showWordmark={!collapsed} />
        </div>

        <GlobalSearch />

        <div className="ml-auto flex items-center gap-1 sm:gap-2">
          <Button size="sm" className="hidden rounded-full sm:inline-flex" asChild>
            <Link href="/#new-post">
              <Plus className="size-4" />
              Bejegyzés
            </Link>
          </Button>
          <Button variant="ghost" size="icon" className="hidden text-muted-foreground sm:inline-flex" asChild>
            <Link href="/communities" aria-label="Közösségek">
              <UsersRound className="size-4" />
            </Link>
          </Button>
          <Button variant="ghost" size="icon" className="text-muted-foreground" asChild>
            <Link href="/chat" aria-label="Üzenetek">
              <MessageCircle className="size-4" />
            </Link>
          </Button>
          <NotificationBell />

          <div className="ml-1 hidden items-center gap-3 border-l border-black/[0.06] pl-3 sm:flex">
            <UserAvatar name={user?.fullName ?? "Felhasználó"} />
            <div className="hidden min-w-0 lg:block">
              <p className="truncate text-sm font-semibold leading-none">
                {currentUserQuery.isLoading
                  ? "Betöltés..."
                  : user?.fullName ?? "Felhasználó"}
              </p>
              <p className="mt-1 truncate text-xs text-muted-foreground">
                {user?.professionalTitle ?? user?.occupation ?? "PraxisTárs"}
              </p>
            </div>
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="text-muted-foreground"
            aria-label="Kijelentkezés"
            onClick={() => void handleSignOut()}
          >
            <LogOut className="size-4" />
          </Button>
        </div>
      </div>
    </header>
  );
}
